// =============================================================================
// ui/shell/AppShell.tsx — sidebar, groups, and the active-tab content slot.
// -----------------------------------------------------------------------------
// The chrome every ported tab renders into. Deliberately thin: it owns which tab
// is active and nothing else. No data, no numbers, no selectors — a tab asks for
// what it needs.
//
// ⚠ THE NOTE THAT USED TO BE HERE SAID "STRUCTURE ONLY, NOT THE DESIGN SYSTEM",
// and it was correct until the design system landed. The tokens, the shared
// components and this shell are all relocated from the old client now, so the
// class names below are ITS names (`.app`, `.sidebar`, `.nav-item`, `.main`,
// `.header`, `.content`) rather than the `app-*` set the first port invented —
// which is what lets `shell.css` copy its rules instead of translating them.
//
// ⚠ The per-tab parity gate is still per-TAB. This file makes the frame match;
// it does not claim the gate for any tab inside it.
//
// ── THE SIDEBAR COLLAPSES, AND THAT IS LOAD-BEARING ──
//
// 60px collapsed, 240px on hover or when pinned. Twenty-one nav items held open
// permanently would take a fifth of the screen, and the Trades table is 17
// columns wide. Pinning is a real preference in the old client; here it is local
// state plus localStorage, because `user_preferences` has no column for it and
// inventing one is a schema change, not a styling pass.
// =============================================================================

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FALLBACK_TAB, resolveTab, tabById, visibleGroups, type TabDef } from './tabs';
import { Icon } from './Icon';
import { FeedbackModal } from './FeedbackModal';
import { ENV } from '../../env';

export interface AppShellProps {
  /**
   * `hiddenTabs` from preferences.
   *
   * ⚠ `| undefined` is required under `exactOptionalPropertyTypes`: the caller
   * forwards `prefs?.hiddenTabs`, which is undefined until preferences load, and
   * without it that is a type error rather than the ordinary "not known yet".
   */
  hidden?: readonly string[] | undefined;
  /**
   * Renders the active tab. Given the resolved id, never the requested one,
   * plus the navigator — because ported tabs link to each other ("add your
   * first account" → Accounts) and must not each roll their own.
   *
   * ⚠ `navigate` resolves through the same rules as the sidebar. That guard was
   * DEAD CODE until this argument existed: the sidebar only ever offers ids it
   * rendered, so nothing could hand `navigate` a hidden or unknown one, and a
   * control that deleted the resolution left every test green. Programmatic
   * navigation is the caller that makes it reachable — and the caller most
   * likely to name a tab the user has switched off.
   */
  children: (tabId: string, navigate: (id: string) => void) => ReactNode;
  /** Starting tab; resolved through the same rules as any navigation. */
  initialTab?: string;
  /**
   * The header strip — in practice the global filter bar.
   *
   * ⚠ Passed IN rather than rendered here, so this file keeps its promise of
   * owning no data: the toolbar reads the filter contexts, and the shell need not
   * know they exist.
   *
   * ⚠ A FUNCTION of the resolved tab id, not a node. The toolbar has to know which
   * tab is open — Analytics and Calendar ignore the global date filter, and the
   * control must say so rather than look live.
   *
   * ⚠ AND IT TAKES `navigate`, for the same reason `children` does. Add Trade and
   * Import are global controls whose screens live on ONE tab, so pressing them
   * from the Dashboard has to move the user there. Without this argument the
   * toolbar could render those buttons and not wire them, which is how they came
   * to be absent in the first place.
   */
  toolbar?: (tabId: string, navigate: (id: string) => void) => ReactNode;
  /**
   * The sidebar foot — in practice the environment label and the signed-in email.
   *
   * ⚠ Passed IN for the same reason as `toolbar`: this file owns no data. It used
   * to sit in a separate `.app-header` strip above the whole shell, which the old
   * client does not have — its brand and build info live in the sidebar. That
   * strip also broke the layout once the shell became a `height: 100vh` grid.
   */
  meta?: ReactNode;
  /**
   * A strip above the header, spanning the content column.
   *
   * ⚠ ABOVE THE HEADER AND OUTSIDE THE KEYED CONTENT, which is the whole point:
   * the demo banner is the only exit from demo mode, so it must not scroll away
   * under a 150-row table and must not remount on every tab change. Passed IN
   * for the same reason as `toolbar` — this file owns no data.
   */
  banner?: ReactNode;
}


/**
 * The four policy pages, transcribed from the old client (app/index.html:3556).
 *
 * ⚠ `rel="noopener noreferrer"` — the old client sets only `noopener`. The
 * addition is deliberate and costs nothing: without `noreferrer` the opened
 * page learns which page in the app the user came from via the Referer header,
 * and for the same-origin marketing site that is pure leakage with no purpose.
 */
const LEGAL_LINKS: readonly { href: string; label: string }[] = [
  { href: 'https://tradecoach.pro/terms', label: 'Terms' },
  { href: 'https://tradecoach.pro/privacy', label: 'Privacy' },
  { href: 'https://tradecoach.pro/refund', label: 'Refund' },
  { href: 'https://tradecoach.pro/risk-disclosure', label: 'Risk' },
];

export function AppShell(
  { hidden, children, initialTab, toolbar, meta, banner }: AppShellProps,
) {
  // ⚠ Resolved on the way IN as well as on every navigation. A saved "last tab"
  // that has since been hidden is the case the old client got wrong — it
  // restored you onto a tab you had switched off.
  const [tabId, setTabId] = useState(() => resolveTab(initialTab ?? FALLBACK_TAB, { hidden }));

  const groups = useMemo(() => visibleGroups({ hidden }), [hidden]);

  const navigate = useCallback(
    (id: string) => setTabId(resolveTab(id, { hidden })),
    [hidden],
  );

  // ⚠ localStorage, not `user_preferences`. The old client persists this in
  // settings, but there is no column for it here and adding one is a schema
  // change (D6 is additive-only, so it would be a migration) — not something a
  // styling pass should smuggle in. Local persistence keeps the behaviour and
  // leaves the decision open.
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem('tc.sidebarPinned') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('tc.sidebarPinned', pinned ? '1' : '0'); } catch { /* a nicety */ }
  }, [pinned]);

  /** Send feedback. Shell state, because the button lives in the sidebar. */
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const title = tabById(tabId)?.label ?? '';

  return (
    <div className={`app${pinned ? ' sidebar-pinned' : ''}`}>
      {/* ⚠ RENDERED FROM THE SHELL, not from a tab, because it is chrome: it has
          to be reachable from every screen including one that has crashed its
          own content. It portals to the body, so its position here is about
          ownership rather than layout. */}
      {feedbackOpen ? (
        <FeedbackModal
          currentTab={tabId}
          appVersion={`${ENV.name}-${__APP_BUILD__}`}
          onClose={() => setFeedbackOpen(false)}
        />
      ) : null}
      <div className="sidebar">
        <div className="logo">
          <div className="logo-icon">
            {/* The ascending-bars mark, transcribed from the old client's inline
                SVG. `#f97316` is `--brand`, hardcoded there and kept here so the
                mark holds its colour in both themes. */}
            <svg viewBox="0 0 542 560" width="30" height="30" fill="#f97316" aria-hidden="true">
              <path d="m369.041369,26c-22.76118,0-43.804332,12.106901-55.244548,31.784035l-42.796822,73.610528h240.201597V26h-142.160227Z" />
              <path d="m30.798403,236.789126h142.160227c22.76118,0,43.804332-12.10675,55.244548-31.784035l42.796822-73.610528H30.798403v105.394563Z" />
              <path d="m313.796822,206.389548l-42.796822,73.610528h149.967148v-105.394563h-51.925779c-22.76118,0-43.804332,12.10675-55.244548,31.784035Z" />
              <path d="m127.932016,385.394638h45.026615c22.76118,0,43.804332-12.106901,55.244548-31.784035l42.796822-73.610528H127.932016v105.394563Z" />
              <path d="m313.796822,354.99491l-42.796822,73.610528h100.492434v-105.394563h-2.451064c-22.76118,0-43.804332,12.106901-55.244548,31.784035Z" />
              <path d="m172.958479,534h.000151c22.76118,0,43.804332-12.10675,55.244548-31.784035l42.796822-73.610528h-98.041521v105.394563Z" />
            </svg>
          </div>
          <div className="logo-text">
            <img className="logo-wordmark lw-dark" src="/logo-wordmark.svg" alt="TradeCoach Pro" />
            <img className="logo-wordmark lw-light" src="/logo-wordmark-light.svg" alt="TradeCoach Pro" />
            <span>REBUILD</span>
          </div>
        </div>

        <button
          type="button" className="sidebar-pin-btn"
          onClick={() => setPinned((p) => !p)}
          title="Pin / unpin sidebar"
          aria-pressed={pinned}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="17" x2="12" y2="22" />
            <path d="M5 17h14l-1.5-9h-11L5 17z" />
            <path d="M12 8V2" />
          </svg>
          <span className="sidebar-pin-label">{pinned ? 'Unpin sidebar' : 'Pin sidebar'}</span>
        </button>

        <nav aria-label="Sections">
          {groups.map(({ group, tabs }, i) => (
            <div key={String(group)}>
              {/* ⚠ A divider only BETWEEN groups. A leading one under the logo
                  doubles the rule the logo already draws. */}
              {i > 0 && !tabs[0]?.sectionLabel ? <div className="nav-divider" /> : null}
              {tabs.map((t) => (
                <SidebarItem key={t.id} tab={t} active={t.id === tabId} onSelect={navigate} />
              ))}
            </div>
          ))}
        </nav>

        {/* 🚨🎯 SEND FEEDBACK IS THE ONE ROUTE A USER HAS TO TELL US ANYTHING.
            It sits in the sidebar foot where the old client puts it, in brand
            orange, because after launch this is the client that ships and a
            person who cannot report a problem does not report it - they stop
            using the product, and that is the feedback nobody ever hears. */}
        <button
          type="button"
          className="sidebar-feedback"
          onClick={() => setFeedbackOpen(true)}
          title="Send us feedback - bug, feature idea, question, anything"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round" width={13} height={13} aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <span className="sidebar-feedback-label">Send feedback</span>
        </button>

        {/* ⚠ NAMED, not omitted - and this list SHRINKS as things land. Send
            feedback came off it the moment the button above shipped; a note that
            keeps claiming a control is missing while it sits six lines up is the
            failure mode this whole tab has hit three times. */}
        <div className="sidebar-meta">
          {meta}
          {/* ── 🚨 THE LEGAL LINKS. NOT DECORATION ── (#54)

              This client had none of these anywhere — no Terms, no Privacy, no
              Refund policy, no risk disclosure — while the old client carries
              all four in this exact spot. For a paid product that is a launch
              blocker rather than a parity nicety: checkout happens inside the
              app, and a payment processor’s domain review looks for these to be
              reachable FROM the app. Stripe is the live processor now, but the
              requirement is the same one the old client’s comment records.

              ⚠ RISK DISCLOSURE IS THE ONE NOT TO DROP. Terms, Privacy and
              Refund are ordinary SaaS pages; futures products carry a risk
              disclosure, and this is a journal for leveraged futures traders on
              prop accounts. It is the fourth link precisely because it is the
              easiest of the four to forget.

              ⚠ ABSOLUTE URLS TO THE MARKETING DOMAIN, matching the old client:
              the app runs on a different host from the policy pages, so a
              relative href would 404 on whichever host it is served from.

              ⚠ IN THE PERSISTENT SIDEBAR, not on a settings pane, so they are
              reachable from every screen rather than from one the user has to
              know to visit. */}
          <div className="sidebar-legal">
            {LEGAL_LINKS.map(({ href, label }, i) => (
              <span key={href}>
                {i > 0 ? <span aria-hidden="true"> · </span> : null}
                <a href={href} target="_blank" rel="noopener noreferrer">{label}</a>
              </span>
            ))}
          </div>
          <div>Help &amp; guides, sign-out and the storage indicator aren’t in the sidebar yet.</div>
        </div>
      </div>

      <div className="main">
        {banner}
        {/* ⚠ The header is OUTSIDE the keyed content. The range is global, so
            remounting it on every tab change would reset the control the user
            just set — silently, since the range itself lives in context and would
            survive while the widget showing it flickered. */}
        <header className="header">
          <h2>{title}</h2>
          {/* ⚠ The old client's header also holds Search, the unit selector, the
              Log counter, Import and Add Trade. Those are per-tab features, not
              chrome, and none is ported — so nothing stands in for them here. */}
          {toolbar ? <div className="actions">{toolbar(tabId, navigate)}</div> : null}
        </header>

        {/* ⚠ THE `key` IS NEARLY A NO-OP, AND ITS OLD COMMENT WAS WRONG TWICE.
            It read: "Deliberate: a ported tab carrying its own local state must
            not show the previous tab's state for a frame, and a stale scroll
            position on a virtualised table reads as data loss."

            Neither holds. `App.tsx` returns a DIFFERENT COMPONENT TYPE per tab —
            twenty-one tabs, twenty-one types, none shared — so React unmounts
            and remounts on every switch with or without a key; one type cannot
            render for two tabIds, so there is no previous state to show. And
            nothing in this client is virtualised: no windowing library, and the
            only match for "virtual" under `src/ui/` was that sentence.
            `git log -S` puts the line in the FIRST shell commit, before any tab
            existed — both reasons were precautions drafted in advance.

            🔴 MEASURED, because the comment made it look like the cause of five
            separate bug reports (tabs forgetting where you were). An isolated
            reproduction of this exact shape, with and without the key:

                keyed=true  → came back showing "A:0"
                keyed=false → came back showing "A:0"

            Identical. Deleting it would have fixed nothing. The real cause is
            that only one tab is mounted at a time, and the fix is
            `state/tabMemory.tsx` — memory held ABOVE the tab.

            ▶ Kept because it is near-free and removing it is tidying with a
            non-zero blast radius, not because it is doing what it claimed. */}
        {/* ── 🚨 TWO ELEMENTS, NOT ONE, AND THE SPLIT IS LOAD-BEARING ──
            `.content` is the SCROLL CONTAINER and is full width. `.tab-section`
            is a plain block inside it, and that is what carries the per-tab
            `max-width`.

            This client originally merged them onto one `<main>`, which looked
            equivalent and is not: putting a `max-width` on the element that owns
            `overflow-y: auto` moves the SCROLLBAR inward with the content, so it
            floats at the right edge of the text instead of the right edge of the
            window. Josh caught it on a side-by-side — the scrollbar position was
            the visible symptom, the merged element was the cause.

            ⚠ `data-tab` lives on the INNER element for the same reason it does in
            the old client: every per-tab rule is written
            `.tab-section[data-tab="trades"] .trade-table-card { … }`, so the hook
            has to sit on the bounded block, not on the scroller. */}
        <main className="content">
          <div className="tab-section" data-tab={tabId} key={tabId}>
            {children(tabId, navigate)}
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarItem({
  tab, active, onSelect,
}: { tab: TabDef; active: boolean; onSelect: (id: string) => void }) {
  return (
    <>
      {tab.sectionLabel ? (
        <div className="nav-section" aria-hidden="true">{tab.sectionLabel}</div>
      ) : null}
      <button
        type="button"
        className={`nav-item${active ? ' active' : ''}`}
        // Screen readers get the selected state; `.active` is only paint.
        aria-current={active ? 'page' : undefined}
        data-tab={tab.id}
        onClick={() => onSelect(tab.id)}
      >
        <Icon name={tab.icon} />
        <span>{tab.label}</span>
      </button>
    </>
  );
}
